export class ReadOnlyState {

  constructor(private manager: ReadOnlyStateManager, private path: string[]) {}

  public isReadOnly(formCtrlName?: string): boolean {
    const fullPath = formCtrlName ? [...this.path, formCtrlName] : this.path;
    return !!this.manager.isReadOnly(...fullPath);
  }

  public getReadOnlyTooltip(formCtrlName?: string): string {
    const fullPath = formCtrlName ? [...this.path, formCtrlName] : this.path;
    return this.manager.getReadOnlyTooltip(...fullPath);
  }

}
export class ReadOnlyStateManager {

  private root: ReadOnlyStateNode = new ReadOnlyStateNode();

  public printAll() {
    this.root.print(0, 'root');
  }

  public of(path: string[]): ReadOnlyState {
    return new ReadOnlyState(this, path);
  }

  public isReadOnly(...path: string[]): boolean {
    return this.getReadOnlyReason(path).length > 0;
  }

  public getReadOnlyTooltip(...path: string[]): string {
    return this.getReadOnlyReason(path).map(reason => ReadOnlyReasonToTooltip[reason]).join('\n\n');
  }

  public getReadOnlyReason(path: string[]): ReadOnlyReason[] {
    let current = this.root;
    const rootReasons: ReadOnlyReason[] = this.root.get();
    const reasonSet: Partial<Record<ReadOnlyReason, boolean>> = {};
    ReadOnlyStateManager.addAllReasonsToSet(reasonSet, rootReasons);
    for (const part of path) {
      const key = part.toLowerCase();
      const next: ReadOnlyStateNode = current[key];
      if (!next) {
        break;
      }
      ReadOnlyStateManager.addAllReasonsToSet(reasonSet, next.get());
      current = next;
    }
    return Object.keys(reasonSet).map(reasonStr => ReadOnlyReason[reasonStr]);
  }

  private static addAllReasonsToSet(reasonSet: Partial<Record<ReadOnlyReason, boolean>>, nodeReasons: ReadOnlyReason[]) {
    for (const reason of nodeReasons) {
      reasonSet[reason] = true;
    }
  }

  public addState(reason: ReadOnlyReason, ...path: string[]): void {
    let current = this.root;
    for (const part of path) {
      const key = part.toLowerCase();
      let next = current[key];
      if (!next) {
        next = new ReadOnlyStateNode();
        current[key] = next;
      }
      current = next;
    }
    current.add(reason);
  }

  public removeState(reason: ReadOnlyReason, ...path: string[]): void {
    let current = this.root;
    for (const part of path) {
      const key = part.toLowerCase();
      let next = current[key];
      if (!next) {
        next = new ReadOnlyStateNode();
        current[key] = next;
      }
      current = next;
    }
    current.rem(reason);
  }

  public clear(): void {
    this.root = new ReadOnlyStateNode();
  }

  public removeAll(...reasons: ReadOnlyReason[]): void {
    // console.log('removeAll.reasons=', reasons);
    const stack = [this.root];
    while (stack.length > 0) {
      const current = stack.pop();
      // console.log('removeAll.current=', JSON.parse(JSON.stringify(current)), 'stack=', stack.slice());
      for (const reason of reasons) {
        current.rem(reason);
      }
      for (const child of Object.values(current)) {
        if (child._reasonSet) {
          stack.push(child);
        }
      }
    }
  }

}
export enum ReadOnlyReason {
  LOCKED = 'LOCKED',
  MANAGED_IN_GREEN_OS = 'MANAGED_IN_GREEN_OS',
  MANAGED_IN_DIGIRES = 'MANAGED_IN_DIGIRES',
  CALCULATED = 'CALCULATED',
  FEED_IN = 'FEED_IN',
  FIN_MODEL = 'FIN_MODEL',
  UNAUTHORIZED = 'UNAUTHORIZED',
  IPT_TEAM = 'IPT_TEAM',
  REJECTED = 'REJECTED',
  CONSTRUCTION = 'CONSTRUCTION',
  SOLD = 'SOLD',
  DECOMMISSIONED = 'DECOMMISSIONED',
  DELETED = 'DELETED',
  PIPELINE_EXIT = 'PIPELINE_EXIT',
  DISABLED = 'DISABLED',
}
export const ReadOnlyReasonToTooltip: {[reason: string]: string} = {
  [ReadOnlyReason.LOCKED]: `The Project Manager has put a temporary 'Validation Lock' on the project information which makes it Read Only (i.e. it can't be changed) until the end of the reporting cycle`,
  [ReadOnlyReason.MANAGED_IN_GREEN_OS]: `This Value is now being managed in GreenOS Project Development so changes can only be made in that system.`,
  [ReadOnlyReason.MANAGED_IN_DIGIRES]: `This Value is no longer being managed in GreenOS Project Development so changes can not be made in this system any more.`,
  [ReadOnlyReason.CALCULATED]: `The value in this data field is calculated in DigiRES and can't be manually overwritten.`,
  [ReadOnlyReason.FEED_IN]: `The value being shown in this data field is just for display purposes - it has been populated in the input field that is elsewhere in DigiRES-`,
  [ReadOnlyReason.FIN_MODEL]: `The value in this data field has been read from the Financial Model and can't be manually overwritten`,
  [ReadOnlyReason.UNAUTHORIZED]: 'You are not authorized to edit this information',
  [ReadOnlyReason.IPT_TEAM]: `You are not authorized to edit this information, the project team can be edited by the IPT Chair, Project Manager, Deputy Project Manager`,
  [ReadOnlyReason.REJECTED]: 'The Project Status has been set to Rejected - changes are not possible to Rejected projects',
  [ReadOnlyReason.CONSTRUCTION]: `The project is now in the Construction Phase and data in this field can only be edited when the project is in the Development Phase as it is retained as a record of the values reported in the Development Phase`,
  [ReadOnlyReason.SOLD]: 'The Project Status has been set to Sold - changes are not possible to Sold projects',
  [ReadOnlyReason.DECOMMISSIONED]: 'The Project Status has been set to Decommissioned - changes are not possible to Decommissioned projects',
  [ReadOnlyReason.DELETED]: 'The Project Status has been set to Deleted - changes are not possible to Deleted projects',
  [ReadOnlyReason.PIPELINE_EXIT]: 'The data in this field can only be edited when the project is not in an active status',
  [ReadOnlyReason.DISABLED]: 'These inputs can not be edited here',
};

const ReadOnlyReasonData: {[reason: string]: {index: number, name: ReadOnlyReason}} = {};
for (const [index, key] of Array.from(Object.keys(ReadOnlyReason).entries())) {
  ReadOnlyReasonData[key] = {
    index: index,
    name: ReadOnlyReason[key],
  };
}
const AllReadOnlyReasonsArr = Object.values(ReadOnlyReasonData);
class ReadOnlyStateNode {
  _reasonSet: boolean[] = AllReadOnlyReasonsArr.map(_ => false);

  add(reason: ReadOnlyReason): void {
    this._reasonSet[ReadOnlyReasonData[reason].index] = true;
  }

  rem(reason: ReadOnlyReason): void {
    this._reasonSet[ReadOnlyReasonData[reason].index] = false;
  }

  get(): ReadOnlyReason[] {
    return AllReadOnlyReasonsArr.filter(reason => this._reasonSet[reason.index])
      .map(reason => reason.name);
  }

  print(indent: number, label: string) {
    console.log('>'.repeat(indent), label, this._reasonSet);
    for (const key of Object.keys(this)) {
      if (key !== '_reasonSet') {
        this[key].print(indent + 1, key);
      }
    }
  }
}
